import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { getLang } from "./utils/lang"

const resources = {
  en: {
    translation: {},
  },
  ko: {
    translation: {},
  },
}
const languages = ["en", "ko", "ja", "zh", "es", "pt"]

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: languages,

    interpolation: {
      prefix: "{",
      suffix: "}",
    },
    fallbackLng: "en",
    react: {
      bindI18n: "languageChanged",
      bindI18nStore: "added",
    },
  })

/**
 * CDN 이 죽었을 때 쓰는 사본 — 빌드 산출물의 /lang/<언어>/*.json 을 읽는다.
 * 이 파일들은 빌드 직전 `pnpm run i18n` 이 받아 public/lang 에 채워 넣는다(amplify.yml).
 * fetch 는 404 에도 예외를 던지지 않으므로 ok 를 직접 확인해야 한다 — 안 그러면
 * 404 응답 본문(HTML)을 json() 으로 파싱하다 터져 원인을 알기 어려운 오류가 된다.
 */
const loadFromBuild = async (lang: string) => {
  const names = ["base", "button", "messages", "metric", "rules"] as const
  const responses = await Promise.all(
    names.map((name) => fetch(`${import.meta.env.VITE_BASE_URL}/lang/${lang}/${name}.json`)),
  )
  if (responses.some((res) => !res.ok)) {
    throw new Error(`No bundled language files for "${lang}"`)
  }

  const [base, button, messages, metric, rules] = await Promise.all(
    responses.map((res) => res.json()),
  )

  return {
    ...base,
    button,
    messages,
    metric,
    rules,
    metricKey: base.metric, // metric 객체에 의해 base.metric이 덮어씌워지는 문제 해결
  }
}

const loadAsync = async (lang: string) => {
  if (lang === "zh") {
    lang = "zh-Hant"
  }

  try {
    const langData = await getLang(lang)

    return {
      ...langData.base,
      button: langData.button,
      messages: langData.messages,
      metric: langData.metric,
      rules: langData.rules,
      metricKey: langData.base.metric, // metric 객체에 의해 base.metric이 덮어씌워지는 문제 해결
    }
  } catch (error) {
    console.error(`Failed to load "${lang}" from CDN, falling back to the bundled copy:`, error)

    try {
      return await loadFromBuild(lang)
    } catch (fallbackError) {
      // 해당 언어 사본까지 없으면 영어라도 띄운다. 번들을 못 읽으면 i18next 가 키 문자열을
      // 그대로 내보내 화면이 "activityName" 같은 내부 키로 뒤덮이기 때문이다.
      if (lang !== "en") {
        console.error(`No bundled copy for "${lang}", falling back to English:`, fallbackError)

        return await loadFromBuild("en")
      }
      throw fallbackError
    }
  }
}

languages.forEach((lang) => {
  loadAsync(lang)
    .then((data) => {
      i18n.addResourceBundle(lang, "translation", data)
    })
    .catch((error) => {
      console.error(`Could not load any translations for "${lang}":`, error)
    })
})

export default i18n
